import { buildUrl } from '@/services/queryUtils';
import type { FilterParams } from '@/services/revenueApi';
import type { ChartGranularity, LaunchWindowDays, SelectedItem } from '@/types/content';
import type { Episode, Show } from '@/types';
import type { ItemPlatformParams } from './PerformanceTable/ExpandedMetricRowRender';
import type { PaginatedQueryParams } from './PerformanceTable/ServerSidePaginatedTable';

export type MetricQueryParams = { filters: FilterParams; granularity: ChartGranularity };
export type SelectedItemParams = { selectedItems: SelectedItem[] };

/**
 * Temporary to control use of iceberg tables as part of the backfill
 * The new Echo Tables use the `v3` dataSource.
 * The legacy `v2` dataSource can be removed with the legacy performance card.
 */
export type ContentQueryDataSources = 'v2' | 'v3';

type DataSourceParams = { dataSource: ContentQueryDataSources };
type UrlBuilder<T> = (params: T) => string;

export function dataSource<T>(
  dataSource: ContentQueryDataSources,
  builder: UrlBuilder<T & DataSourceParams>
): UrlBuilder<T> {
  return (params) => builder({ ...params, dataSource });
}

/** Builds `/v2/content/metrics` with sorted network/show ids. */
export const totalMetricsUrlBuilder: UrlBuilder<MetricQueryParams> = ({ filters, granularity }) =>
  buildUrl('/v2/content/metrics', {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: filters.shows
      ?.map((item) => item.id)
      .sort()
      .join(','),
    ...filters.dateRange,
    granularity,
  });

export const publishDatesUrlBuilder: UrlBuilder<FilterParams> = (filters) =>
  buildUrl('/v2/content/episodes/publish-dates', {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: filters.shows
      ?.map((network) => network.id)
      .sort()
      .join(','),
    from: filters.dateRange?.from,
    to: filters.dateRange?.to,
  });

export const episodeMetricsUrlBuilder: UrlBuilder<
  MetricQueryParams & SelectedItemParams & DataSourceParams
> = ({ filters, selectedItems, granularity, dataSource }) =>
  buildUrl('/v2/content/episodes/metrics', {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: filters.shows
      ?.map((show) => show.id)
      .sort()
      .join(','),
    episodeIds: selectedItems
      ?.map((item) => item.id)
      .sort()
      .join(','),
    ...filters.dateRange,
    granularity,
    dataSource,
  });

export const episodeMetricsBreakdownUrlBuilder = ({
  filters,
  item,
  limit,
}: ItemPlatformParams<Episode>) =>
  buildUrl(`/v2/content/episodes/${item.id}/platforms`, {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: [item.showId],
    from: filters.dateRange!.from,
    to: filters.dateRange!.to,
    limit,
  });

/** Builds `/v2/content/shows/platforms` for a single expanded show row. */
export const showMetricsBreakdownUrlBuilder = ({
  filters,
  item,
  limit,
}: ItemPlatformParams<Show>) =>
  buildUrl('/v2/content/shows/platforms', {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: [item.id],
    from: filters.dateRange!.from,
    to: filters.dateRange!.to,
    limit,
  });

export const networkMetricsUrlBuilder = ({
  filters,
  selectedItems,
  granularity,
}: MetricQueryParams & SelectedItemParams) =>
  buildUrl('/v2/content/networks/metrics', {
    networkIds: selectedItems
      .map((item) => item.id)
      .sort()
      .join(','),
    ...filters.dateRange,
    granularity,
  });

export const showMetricsUrlBuilder = ({
  filters,
  selectedItems,
  granularity,
}: MetricQueryParams & SelectedItemParams) =>
  buildUrl('/v2/content/shows/metrics', {
    networkIds: filters.networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: selectedItems
      .map((item) => item.id)
      .sort()
      .join(','),
    ...filters.dateRange,
    granularity,
  });

export const showsUrlBuilder = ({
  filters: { networks, shows, dateRange },
  pagination,
}: PaginatedQueryParams) =>
  buildUrl('/v2/content/shows', {
    from: dateRange?.from,
    to: dateRange?.to,
    sortBy: pagination?.sortField?.key,
    sortDirection: pagination?.sortField?.direction,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    networkIds: networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: shows
      ?.map((show) => show.id)
      .sort()
      .join(','),
  });

/**
 * Builds `/v2/content/episodes/launch-window` — paginated episodes with metrics aggregated over
 * the first `windowDays` days from publish. Ignores the date range (same "All time" semantics as
 * the post-launch comparison chart). Deliberately omits `sortBy`: the API only accepts
 * `sortDirection` for this endpoint since metric sorting is disabled on the post-launch table.
 */
export const episodesLaunchWindowUrlBuilder = ({
  filters: { networks, shows },
  pagination,
  additionalParams,
}: PaginatedQueryParams<{ windowDays: LaunchWindowDays }>) =>
  buildUrl('/v2/content/episodes/launch-window', {
    windowDays: additionalParams.windowDays,
    sortDirection: pagination?.sortField?.direction,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    networkIds: networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: shows
      ?.map((show) => show.id)
      .sort()
      .join(','),
    dataSource: 'v3',
  });

export const networksUrlBuilder = ({
  filters: { networks, dateRange },
  pagination,
}: PaginatedQueryParams) =>
  buildUrl('/v2/content/networks', {
    from: dateRange?.from,
    to: dateRange?.to,
    sortBy: pagination?.sortField?.key,
    sortDirection: pagination?.sortField?.direction,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    networkIds: networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
  });

export const episodesUrlBuilder = ({
  filters: { networks, shows, dateRange },
  pagination,
}: PaginatedQueryParams) =>
  buildUrl('/v2/content/episodes', {
    from: dateRange?.from,
    to: dateRange?.to,
    sortBy: pagination?.sortField?.key,
    sortDirection: pagination?.sortField?.direction,
    page: pagination?.page,
    pageSize: pagination?.pageSize,
    networkIds: networks
      ?.map((network) => network.id)
      .sort()
      .join(','),
    showIds: shows
      ?.map((show) => show.id)
      .sort()
      .join(','),
    dataSource: 'v3',
  });
